import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../lib/config';
import { ChevronLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [cms, setCms] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const ENABLE_CMS = process.env.REACT_APP_ENABLE_CMS === '1';

  useEffect(() => {
    if (!ENABLE_CMS) { setLoading(false); return; }
    let aborted = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_BASE}/content/privacy-policy`);
        if (!r.ok) throw new Error('failed');
        const data = await r.json();
        if (!aborted) setCms({ title: data.title || '', content: data.content || '' });
      } catch (_) {
        if (!aborted) setCms({ title: '', content: '' });
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{ENABLE_CMS && cms.title?.trim() ? cms.title : 'プライバシーポリシー'}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center mb-8">
            <ShieldCheckIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{cms.title?.trim() || 'バイターズ プライバシーポリシー'}</h2>
            {!cms.content && (
              <p className="text-gray-600">最終更新日：2025/09/01</p>
            )}
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-500">読み込み中...</div>
          ) : (ENABLE_CMS && cms.content) ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: cms.content }} />
          ) : (
          <div className="space-y-8 text-gray-800 leading-relaxed">
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">1. 目的</h3>
              <p>
                本プライバシーポリシーは、バイターズ（以下「当社」）が提供するサービスにおける、ユーザーの個人情報の取り扱い方針を定めるものです。当社は関連法令・ガイドラインを遵守し、適正に個人情報を取り扱います。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">2. 取得する情報</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>アカウント情報（氏名、メールアドレス、生年月日、電話番号 等）</li>
                <li>プロフィール・就労関連情報（居住エリア、希望職種、保有資格 等）</li>
                <li>本人確認に関する情報（提出書類、撮影画像 等）</li>
                <li>決済・振込に関する情報（ウォレット残高、取引履歴、銀行口座情報 等）</li>
                <li>端末・ブラウザ情報、クッキー、IPアドレス、位置情報（許諾時）</li>
                <li>お問い合わせ内容、アンケート回答、各種ログ</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">3. 利用目的</h3>
              <ul className="list-disc pl-6 space-y-1">
                <li>サービスの提供、本人確認、不正利用の防止</li>
                <li>求人情報の提示、マッチング、就労管理、報酬の精算</li>
                <li>各種連絡、サポート対応、重要なお知らせの配信</li>
                <li>サービス品質向上、新機能開発、利便性改善のための分析</li>
                <li>法令等に基づく対応</li>
              </ul>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">4. クッキー等の利用</h3>
              <p>
                当社は、利便性向上および利用状況の把握のため、クッキーや類似技術を使用します。ブラウザ設定によりクッキーを無効化できますが、サービスの一部機能が利用できなくなる場合があります。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">5. 第三者提供・委託</h3>
              <p>
                当社は、法令に基づく場合、ユーザーの同意がある場合、または業務委託に必要な範囲において、適切な管理の下で個人情報を第三者に提供・委託することがあります。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">6. セキュリティ管理</h3>
              <p>
                当社は、個人情報の漏えい、滅失または毀損の防止等のため、必要かつ適切な安全管理措置を講じます。また、継続的に体制を見直し、改善に努めます。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">7. 保有期間</h3>
              <p>
                個人情報は、利用目的の達成に必要な範囲で適切に保有し、保存期間の経過または必要がなくなった場合には、適切な方法で消去・廃棄します。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">8. ユーザーの権利</h3>
              <p>
                ユーザーは、当社が保有する自己の個人情報について、開示・訂正・利用停止・削除等を求めることができます。具体的な手続はアプリ内の「お問い合わせ」よりご連絡ください。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">9. 未成年の利用</h3>
              <p>
                未成年のユーザーがサービスを利用する場合は、保護者等の同意を得た上でご利用ください。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">10. 改定</h3>
              <p>
                本ポリシーの内容は、必要に応じて改定されることがあります。重要な変更がある場合は、アプリ内通知等によりお知らせします。
              </p>
            </section>

            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">11. お問い合わせ</h3>
              <p>
                本ポリシーに関するご質問は、「マイページ ＞ お問い合わせ」よりご連絡ください。
              </p>
            </section>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;